import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { FileSpreadsheet } from 'lucide-react';
import Modal from './Modal';
import { 
  ImportResultDto,
  leadsApi, 
  handleApiError, 
  formatDate
} from '../services/leadsService';

interface ImportLeadsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

const UploadSection = styled.div`
  text-align: center;
  padding: 40px 20px;
  border: 2px dashed var(--border-color);
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--primary-color);
    background: var(--primary-light);
  }
`;

const UploadIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  margin-bottom: 16px;
`;

const FileNameIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  color: var(--primary-color);
`;

const UploadText = styled.div`
  font-size: 16px;
  color: var(--text-primary);
  margin-bottom: 8px;
`;

const UploadSubtext = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 20px;
`;

const FileInput = styled.input`
  display: none;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 10px 16px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: var(--primary-color);
          color: white;
          border: 1px solid var(--primary-color);
          &:hover:not(:disabled) {
            background: transparent;
            color: var(--primary-color);
          }
        `;
      case 'danger':
        return `
          background: var(--error-color);
          color: white;
          &:hover:not(:disabled) { background: #d32f2f; }
        `;
      default:
        return `
          background: var(--surface-color);
          color: var(--text-primary);
          border: 1px solid var(--border-color);
          &:hover:not(:disabled) { background: var(--hover-background); }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  
  @media (max-width: 767px) {
    flex-direction: column;
  }
`;

const SelectedFile = styled.div`
  background: var(--background-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const FileInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const FileSize = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: var(--error-color);
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  
  &:hover {
    background: var(--error-light);
    border-radius: var(--border-radius-sm);
  }
`;

const ResultsSection = styled.div`
  margin-top: 20px;
`;

const ResultsSummary = styled.div`
  background: var(--background-color);
  border-radius: var(--border-radius-md);
  padding: 16px;
  margin-bottom: 16px;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
`;

const SummaryItem = styled.div`
  text-align: center;
`;

const SummaryNumber = styled.div<{ color: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.color};
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
`;

const ErrorsList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  margin-bottom: 16px;
`;

const ErrorItem = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid var(--border-color);
  font-size: 14px;
  color: var(--error-color);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DuplicatesList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
`;

const DuplicateItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid var(--border-color);
  
  &:last-child {
    border-bottom: none;
  }
`;

const DuplicateName = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const DuplicateDetails = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 4px;
`;

const Instructions = styled.div`
  background: var(--info-light);
  color: var(--info-color);
  padding: 16px;
  border-radius: var(--border-radius-md);
  margin-bottom: 20px;
  border: 1px solid var(--info-color);
`;

const InstructionsTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
`;

const InstructionsList = styled.ul`
  margin: 0;
  padding-right: 20px;
  
  li {
    margin-bottom: 4px;
    font-size: 14px;
  }
`;

const LoadingText = styled.div`
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
  padding: 20px;
`;

const ImportLeadsModal: React.FC<ImportLeadsModalProps> = ({
  isOpen,
  onClose,
  onImported
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResultDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setError('יש לבחור קובץ CSV בלבד. נא לוודא שהקובץ נשמר עם סיומת .csv');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError(`גודל הקובץ (${formatFileSize(file.size)}) גדול מדי. הגודל המקסימלי המותר הוא 5MB`);
        return;
      }
      
      // Validate file is not empty
      if (file.size === 0) {
        setError('הקובץ שנבחר ריק. נא לבחור קובץ עם נתונים');
        return;
      }
      
      setSelectedFile(file);
      setError(null);
      setImportResult(null);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      setError('נא לבחור קובץ לפני התחלת היבוא');
      return;
    }

    try {
      setImporting(true);
      setError(null);

      const fileContent = await readFileAsText(selectedFile);
      
      // Basic validation of CSV content
      if (!fileContent.trim()) {
        setError('הקובץ ריק. נא לוודא שהקובץ מכיל נתונים');
        return;
      }

      const lines = fileContent.trim().split('\n');
      if (lines.length < 2) {
        setError('הקובץ חייב להכיל לפחות שורת כותרות ושורה אחת של נתונים');
        return;
      }

      const result = await leadsApi.import(fileContent);
      
      setImportResult(result);
      
      if (result.successCount > 0) {
        onImported();
      }
    } catch (err) {
      const errorMessage = handleApiError(err);
      setError(`שגיאה ביבוא הקובץ: ${errorMessage}`);
    } finally {
      setImporting(false);
    }
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        if (!result) {
          reject(new Error('לא הצליח לקרוא את תוכן הקובץ'));
          return;
        }
        resolve(result);
      };
      reader.onerror = () => {
        reject(new Error('שגיאה בקריאת הקובץ. נא לוודא שהקובץ לא פגום'));
      };
      reader.readAsText(file, 'UTF-8');
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleReset = () => {
    setSelectedFile(null);
    setImportResult(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDownloadExample = () => {
    try {
      // Create example CSV content
      const csvContent = [
        'שם מלא,טלפון,דוא"ל,מקור ליד,הערות',
        'יוסי כהן,050-1234567,yossi@example.com,פייסבוק,לקוח פוטנציאלי מעניין',
        'שרה לוי,052-9876543,sara@example.com,גוגל,התעניינות בביטוח לאומי',
        'דוד אברהם,054-5555555,,אתר אינטרנט,צריך ייעוץ משפטי'
      ].join('\n');
      
      // Create and download the file
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'leads_import_example.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the URL object
      setTimeout(() => URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Error downloading example file:', error);
      setError('שגיאה בהורדת קובץ הדוגמה. נסה שוב');
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="יבוא לידים מקובץ Excel/CSV"
      width="700px"
      maxWidth="95vw"
    >
      <Instructions>
        <InstructionsTitle>הוראות לשימוש:</InstructionsTitle>
        <InstructionsList>
          <li><strong>לחץ על "הורד קובץ דוגמה"</strong> כדי להוריד תבנית CSV מוכנה</li>
          <li>הכן קובץ CSV עם העמודות הבאות בדיוק באותו סדר:</li>
          <li style={{ paddingRight: '20px' }}>• <strong>שם מלא</strong> (חובה) - השם המלא של הלקוח</li>
          <li style={{ paddingRight: '20px' }}>• <strong>טלפון</strong> (חובה) - מספר טלפון בפורמט 050-1234567</li>
          <li style={{ paddingRight: '20px' }}>• <strong>דוא"ל</strong> (אופציונלי) - כתובת דוא"ל תקינה</li>
          <li style={{ paddingRight: '20px' }}>• <strong>מקור ליד</strong> (אופציונלי) - המקור ממנו הגיע הליד</li>
          <li style={{ paddingRight: '20px' }}>• <strong>הערות</strong> (אופציונלי) - הערות נוספות על הליד</li>
          <li>השורה הראשונה חייבת להכיל כותרות עמודות בעברית</li>
          <li>המערכת תבדוק כפילויות על בסיס מספר הטלפון ולא תיבא לידים כפולים</li>
          <li>גודל הקובץ המקסימלי: 5MB</li>
          <li>יש לשמור את הקובץ בקידוד UTF-8 כדי לשמור על תווים בעברית</li>
        </InstructionsList>
      </Instructions>

      {error && (
        <ErrorsList>
          <ErrorItem>{error}</ErrorItem>
        </ErrorsList>
      )}

      {!importResult ? (
        <>
          {!selectedFile ? (
            <UploadSection onClick={() => fileInputRef.current?.click()}>
              <UploadIcon><FileSpreadsheet size={48} strokeWidth={1.5} /></UploadIcon>
              <UploadText>לחץ לבחירת קובץ CSV</UploadText>
              <UploadSubtext>או גרור ושחרר קובץ כאן</UploadSubtext>
              <FileInput
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
              />
              <ButtonGroup>
                <Button onClick={handleDownloadExample}>הורד קובץ דוגמה</Button>
                <Button variant="primary">בחר קובץ</Button>
              </ButtonGroup>
            </UploadSection>
          ) : (
            <>
              <SelectedFile>
                <FileInfo>
                  <FileName><FileNameIcon><FileSpreadsheet size={18} strokeWidth={2} /></FileNameIcon>{selectedFile.name}</FileName>
                  <FileSize>{formatFileSize(selectedFile.size)}</FileSize>
                </FileInfo>
                <RemoveButton onClick={handleRemoveFile}>×</RemoveButton>
              </SelectedFile>
              
              <ButtonGroup>
                <Button onClick={handleRemoveFile}>בחר קובץ אחר</Button>
                <Button 
                  variant="primary" 
                  onClick={handleImport}
                  disabled={importing}
                >
                  {importing ? 'מייבא...' : 'התחל יבוא'}
                </Button>
              </ButtonGroup>
            </>
          )}
        </>
      ) : (
        <ResultsSection>
          <ResultsSummary>
            <SummaryGrid>
              <SummaryItem>
                <SummaryNumber color="var(--success-color)">
                  {importResult.successCount}
                </SummaryNumber>
                <SummaryLabel>יובאו בהצלחה</SummaryLabel>
              </SummaryItem>
              
              <SummaryItem>
                <SummaryNumber color="var(--error-color)">
                  {importResult.errorCount}
                </SummaryNumber>
                <SummaryLabel>שגיאות</SummaryLabel>
              </SummaryItem>
              
              <SummaryItem>
                <SummaryNumber color="var(--warning-color)">
                  {importResult.duplicateCount}
                </SummaryNumber>
                <SummaryLabel>כפילויות</SummaryLabel>
              </SummaryItem>
            </SummaryGrid>
          </ResultsSummary>

          {importResult.errors.length > 0 && (
            <>
              <h4>שגיאות:</h4>
              <ErrorsList>
                {importResult.errors.map((error, index) => (
                  <ErrorItem key={index}>{error}</ErrorItem>
                ))}
              </ErrorsList>
            </>
          )}

          {importResult.duplicateLeads.length > 0 && (
            <>
              <h4>לידים כפולים שנמצאו:</h4>
              <DuplicatesList>
                {importResult.duplicateLeads.map((duplicate) => (
                  <DuplicateItem key={duplicate.id}>
                    <DuplicateName>{duplicate.fullName}</DuplicateName>
                    <DuplicateDetails>
                      {(duplicate.contacts?.find(c => c.isPrimary)?.number ?? duplicate.contacts?.[0]?.number ?? '')} • נוצר ב{formatDate(duplicate.createdAt)}
                    </DuplicateDetails>
                  </DuplicateItem>
                ))}
              </DuplicatesList>
            </>
          )}

          <ButtonGroup style={{ marginTop: '20px' }}>
            <Button onClick={handleReset}>יבא קובץ נוסף</Button>
            <Button variant="primary" onClick={handleClose}>סגור</Button>
          </ButtonGroup>
        </ResultsSection>
      )}

      {importing && (
        <LoadingText>מייבא נתונים, אנא המתן...</LoadingText>
      )}
    </Modal>
  );
};

export default ImportLeadsModal;